import {
  KdsButton,
  KdsLabel,
  KdsRadioGroup,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiAddCircleFill, RiErrorWarningLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { Controller, useFieldArray } from "react-hook-form";
import { OrderItemStatusEnum } from "@/config/apiClient.types";
import { DeliveryNote } from "./_partials";
import {
  type DeliveryNoteType,
  type ModalUploadDeliveryNoteFormContentProps,
} from "./ModalUploadDeliveryNoteFormContent.types";

export const ModalUploadDeliveryNoteFormContent = (
  props: ModalUploadDeliveryNoteFormContentProps
) => {
  const { t } = useTranslate();

  const { fields, append, remove } = useFieldArray({
    control: props.methods.control,
    name: "deliveryNotes",
  });

  const orderItemsIds = props.methods
    .watch("deliveryNotes")
    .flatMap(
      (deliveryNote: DeliveryNoteType) => deliveryNote.orderItemIds || []
    );

  const deliveryNotes = props.methods.getValues("deliveryNotes");
  const unselectedOrderItems = props.orderItems
    .filter((orderItem) => orderItem.status === OrderItemStatusEnum.IN_PROGRESS)
    .map((item) => ({
      id: item.id,
      textValue: item.sandboxItem?.name ?? "",
      label: item.sandboxItem?.name ?? "",
    }))
    .filter(
      (item) =>
        !deliveryNotes
          .flatMap(
            (deliveryNote: DeliveryNoteType) => deliveryNote.orderItemIds
          )
          .filter(Boolean)
          .includes(item.id)
    );

  return (
    <>
      {fields.map((field, index) => (
        <DeliveryNote
          key={field.id}
          index={index}
          methods={props.methods}
          orderItems={props.orderItems}
          items={unselectedOrderItems}
          remove={remove}
        />
      ))}

      <div className="mt-4 flex flex-col gap-sp-xl2">
        {fields.length < 10 && (
          <KdsButton
            iconLeft={RiAddCircleFill}
            onPress={() =>
              append({
                deliveryDate: undefined,
                fileSignedId: undefined,
                orderItemIds: [],
              })
            }
            variant="outlined"
          >
            {t("cardAddDeliveryNote.buttons.addDeliveryNote")}
          </KdsButton>
        )}

        <div className="flex flex-col gap-sp-xs2">
          <KdsLabel>
            <div className="flex items-center gap-sp-xs">
              <KdsText size="subtitle-sm">
                {t("cardAddDeliveryNote.validate.validateLabel", {
                  count: orderItemsIds.length,
                })}
              </KdsText>
              <KdsText>
                {t("cardAddDeliveryNote.validate.warningLabel")}
              </KdsText>
            </div>
          </KdsLabel>

          <Controller
            name="validate"
            render={({ field, fieldState }) => (
              <KdsRadioGroup
                dataCy="validateRadioGroup"
                helperMessageProps={
                  fieldState.error
                    ? {
                        iconLeft: RiErrorWarningLine,
                        message: fieldState.error.message,
                        state: "error",
                      }
                    : undefined
                }
                onChange={field.onChange}
                radioProps={[
                  {
                    isError: !!fieldState.error,
                    label: t("cardAddDeliveryNote.validate.yesLabel"),
                    value: "deliveryNote",
                  },
                  {
                    isError: !!fieldState.error,
                    label: t("cardAddDeliveryNote.validate.noLabel"),
                    value: "noDeliveryNote",
                  },
                ]}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};
